// src/lib/locale.js
// (React 훅 없이) 서버/클라이언트 공용 순수 함수들만 모아둔 유틸

// export const LOCALES = ["ko", "en", "ja", "zh"]; 사용 다국어 설정
// export const LOCALES = ["ko", "en", "ja", "zh"];
export const LOCALES = ["ko", "en", "ja", "zh", "th"];
export const DEFAULT_LOCALE = "ko";
export const LOCALE_COOKIE = "locale";

/** 파라미터가 유효한 로케일인지 */
export const isValidLocale = (l) => LOCALES.includes(l);

/** 경로가 외부 링크/전화/메일인지 */
export const isExternal = (href = "") =>
  /^https?:\/\//i.test(href) || href.startsWith("tel:") || href.startsWith("mailto:");

/** 내부 링크를 / 로 정규화 (외부 링크는 그대로) */
export const normHref = (href = "/") => {
  if (!href) return "/";
  if (isExternal(href)) return href.trim();
  return href.startsWith("/") ? href.trim() : `/${href.trim()}`;
};

/** pathname 첫 세그먼트에서 locale 추출 (훅 미사용) */
export const getLocaleFromPath = (pathname = "/") => {
  const parts = String(pathname || "/").split("/").filter(Boolean);
  const maybe = parts[0];
  return isValidLocale(maybe) ? maybe : DEFAULT_LOCALE;
};

/** 다국어 label 객체 → 표시용 문자열 (locale 우선, 없으면 폴백) */
export const labelOf = (label, locale = DEFAULT_LOCALE) => {
  if (typeof label === "string") return label;
  if (label && typeof label === "object") {
    return (
      label[locale] ??
      label[DEFAULT_LOCALE] ??
      label.ko ??
      label.en ??
      label.ja ??
      label.zh ??
      label.th ??
      ""
    );
  }
  return "";
};

/** 임의의 다국어 객체에서 해당 locale 값 선택 (문자열/객체 모두 대응) */
export const pickLocale = (obj, locale = DEFAULT_LOCALE) => {
  if (obj == null) return "";
  if (typeof obj !== "object") return String(obj);
  return (
    obj[locale] ??
    obj[DEFAULT_LOCALE] ??
    obj.ko ??
    obj.en ??
    obj.ja ??
    obj.zh ??
    obj.th ??
    ""
  );
};

/** 문자열 번역 헬퍼 별칭 (UI에서 t(문자열객체, locale)) */
export const t = pickLocale;

/** 현재 경로에 locale prefix를 붙이거나 제거한 href 만들기 (외부 링크는 그대로) */
export const withLocaleHref = (href = "/", locale = DEFAULT_LOCALE) => {
  if (isExternal(href)) return href;
  const p = normHref(href);
  const parts = p.split("/").filter(Boolean);
  const alreadyPrefixed = parts.length > 0 && LOCALES.includes(parts[0]);

  if (locale === DEFAULT_LOCALE) {
    // 기본 언어는 prefix 제거
    return alreadyPrefixed ? "/" + parts.slice(1).join("/") : p;
  }
  // 비-기본 언어는 prefix 부여. /main/X 는 /{lang}/X 로 — /en/main/X 는 404 (공용 locale-url 과 같은 규칙)
  if (alreadyPrefixed) return p;
  const MAIN_PREFIX_RE = /^\/main(?=[/?#]|$)/;
  return MAIN_PREFIX_RE.test(p) ? `/${locale}${p.replace(MAIN_PREFIX_RE, "")}` : `/${locale}${p}`;
};

/** 현재 pathname의 locale 세그먼트만 바꾸기 (쿼리는 별도에서 합쳐 사용) */
export const replaceLocaleInPath = (pathname = "/", nextLocale = DEFAULT_LOCALE) => {
  const safe = pathname || "/";
  const parts = safe.split("/").filter(Boolean);
  const hasLocale = parts.length > 0 && LOCALES.includes(parts[0]);

  if (nextLocale === DEFAULT_LOCALE) {
    const rest = hasLocale ? parts.slice(1) : parts;
    return "/" + rest.join("/");
  }
  if (hasLocale) {
    parts[0] = nextLocale;
    return "/" + parts.join("/");
  }
  return `/${nextLocale}` + (safe === "/" ? "" : safe);
};

/** 언어별 활성화 맵을 고려한 노출 여부 */
export const isActiveForLocale = (item = {}, locale = DEFAULT_LOCALE) => {
  if (item.active === false) return false; // 전역 비활성
  const map = item.activeByLocale;
  if (!map || typeof map !== "object") return true; // 맵 없으면 전부 노출
  const v = map[locale];
  return typeof v === "boolean" ? v : true;
};

/** 관리자에서 문자열/객체 혼재 라벨을 안전하게 객체 형태로 보정할 때 사용(선택) */
export const ensureLocaleObj = (v) => {
  const out = { ko: "", en: "", ja: "", zh: "", th: "" };
  if (!v) return out;
  if (typeof v === "string") { out.ko = v; return out; }
  LOCALES.forEach((l) => { if (typeof v[l] === "string") out[l] = v[l]; });
  return out;
};

/** 빈 값 제거해서 Firestore 저장 시 용량 절약(선택) */
export const compactLocaleObj = (obj) => {
  const o = {};
  LOCALES.forEach((l) => {
    const val = obj?.[l];
    if (typeof val === "string" && val.trim()) o[l] = val.trim();
  });
  return o;
};
